import React from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import GallerySection from '../components/GallerySection';
import { getOptimizedImageUrl } from '../lib/cms';

export default function HomePage() {
  return (
    <>
      <SeoHead
        title="Ibjerget.dk | Golden Eagle Terapier på Midtfyn"
        description="Ibjerget på Midtfyn – et spirituelt behandlerfællesskab på Ibjerget i Årslev med kropsterapi, ALT ansigtsterapi, massage, tarotkortterapi, foredrag, bøger og kunst."
        canonicalUrl="https://www.ibjerget.dk/"
      />

      {/* 1. HERO SECTION */}
      <section className="relative min-h-[90vh] lg:min-h-screen flex items-center justify-center overflow-hidden bg-[#381d6e] text-white pt-24 pb-16">
        {/* Background Image with warm overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/forside-hus.webp"
            alt="Ibjerget – huset i naturen på Midtfyn"
            className="w-full h-full object-cover object-center opacity-30 filter brightness-90"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#381d6e] via-[#381d6e]/65 to-[#381d6e]/80" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center mt-6">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#d9238c]/30 border border-[#d9238c]/40 text-[#f7eefa] text-xs font-semibold uppercase tracking-wider mb-6">
            <i className="fa-solid fa-mountain-sun" aria-hidden="true"></i>
            <span>Ibjerget.dk • Golden Eagle Terapier</span>
          </span>

          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-[#faf5f9] leading-[1.1] max-w-4xl mx-auto">
            Et sted for dig, din krop og din udvikling
          </h1>

          <p className="mt-6 text-base sm:text-lg md:text-xl text-[#f7eefa] max-w-2xl mx-auto font-normal leading-relaxed">
            På Ibjerget forener vi jordnær kropsterapi, refleksiv tarotkortterapi, foredrag samt spirituelle bøger og kunst i rolige fynske rammer.
          </p>

          {/* Primary CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto px-8 py-4 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              <span>Book behandling</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
            </Link>
            <Link
              to="/spirituelle-boeger"
              className="w-full sm:w-auto px-8 py-4 rounded-full bg-white/10 hover:bg-white/20 text-[#faf5f9] border border-white/20 font-semibold text-base backdrop-blur-sm transition-all duration-300 flex items-center justify-center gap-2"
            >
              <span>Spirituelle bøger og kunst</span>
              <i className="fa-solid fa-book-open text-xs text-[#f7eefa]" aria-hidden="true"></i>
            </Link>
          </div>

          {/* Micro stats / Trust badges */}
          <div className="mt-14 pt-8 border-t border-white/10 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-4 max-w-xl mx-auto text-xs text-[#f7eefa]">
            <div className="space-y-1">
              <span className="font-serif text-lg font-bold text-white block">Midtfyn</span>
              <span className="text-[11px] text-[#f7eefa]">Ibjergvej 12, Årslev</span>
            </div>
            <div className="space-y-1 sm:border-x border-white/10 sm:px-2 pt-3 sm:pt-0 border-t sm:border-t-0">
              <span className="font-serif text-lg font-bold text-white block">Krop & Sind</span>
              <span className="text-[11px] text-[#f7eefa]">Kropsterapi & Tarot</span>
            </div>
            <div className="space-y-1 pt-3 sm:pt-0 border-t sm:border-t-0 border-white/10">
              <span className="font-serif text-lg font-bold text-white block">Forlag & Galleri</span>
              <span className="text-[11px] text-[#f7eefa]">Bøger & Spirituel Kunst</span>
            </div>
          </div>
        </div>
      </section>

      {/* 2. TRE PRIMÆRE INDGANGE */}
      <section className="py-20 sm:py-24 bg-[#faf5f9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#d9238c] block mb-2">
              Fokusområder
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1f142e] font-semibold tracking-tight">
              Veje til fordybelse og nærvær
            </h2>
            <p className="mt-3 text-base text-[#4e3568]">
              Udforsk vores behandlinger, foredrag og udgivelser på Ibjerget
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            
            {/* 1. Kropsterapi & Kropsbehandlinger (Col 1-7) */}
            <div className="lg:col-span-7 bg-[#faf5f9] rounded-3xl overflow-hidden border border-[#edd5e8] shadow-sm flex flex-col md:flex-row group transition-all duration-300">
              <div className="md:w-1/2 relative overflow-hidden min-h-[260px] md:min-h-full">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/behandlerrum.webp', { width: 800, quality: 85, fit: 'cover' })}
                  alt="Kropsterapi og behandlerrum på Ibjerget"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
                <div className="absolute top-4 left-4 bg-[#381d6e]/85 backdrop-blur-sm text-[#faf5f9] text-xs font-medium px-3 py-1 rounded-full">
                  Niels Boye
                </div>
              </div>
              <div className="p-8 md:w-1/2 flex flex-col justify-between">
                <div>
                  <div className="w-10 h-10 rounded-full bg-[#d9238c]/20 flex items-center justify-center text-[#9b1b66] mb-4">
                    <i className="fa-solid fa-hand-holding-heart text-base" aria-hidden="true"></i>
                  </div>
                  <h3 className="font-serif text-2xl font-semibold text-[#1f142e] mb-3">
                    Kropsterapi & Massage
                  </h3>
                  <p className="text-sm text-[#4e3568] leading-relaxed mb-6">
                    ALT Ansigtsterapi, Kropsterapi, Healingmassage og Hotstonemassage med fokus på ro, kropskontakt og forløsning af spændinger.
                  </p>
                </div>
                <Link
                  to="/kropsterapi"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#9b1b66] group-hover:text-[#d9238c] transition-colors"
                >
                  <span>Læs om kropsterapi</span>
                  <i className="fa-solid fa-arrow-right text-xs transform group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

            {/* 2. Tarotkortterapi (Col 8-12) */}
            <div className="lg:col-span-5 bg-white rounded-3xl overflow-hidden border border-[#edd5e8] shadow-sm flex flex-col group transition-all duration-300">
              <div className="relative overflow-hidden h-56">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/tarotkortterapi.webp', { width: 700, quality: 85, fit: 'cover' })}
                  alt="Tarotkortterapi med chakraer og samtale"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
                <div className="absolute top-4 left-4 bg-[#381d6e]/85 backdrop-blur-sm text-[#faf5f9] text-xs font-medium px-3 py-1 rounded-full">
                  Lone Hvid
                </div>
              </div>
              <div className="p-8 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="font-serif text-2xl font-semibold text-[#1f142e] mb-3">
                    Tarotkortterapi
                  </h3>
                  <p className="text-sm text-[#4e3568] leading-relaxed mb-6">
                    Kortene anvendes som et reflekterende og terapeutisk samtaleredskab til chakra-status og problemløsning – uden spådom eller mystik.
                  </p>
                </div>
                <Link
                  to="/tarotkortterapi"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#9b1b66] group-hover:text-[#d9238c] transition-colors"
                >
                  <span>Læs om tarotkortterapi</span>
                  <i className="fa-solid fa-arrow-right text-xs transform group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

            {/* 3. Spirituelle bøger og kunst (Col 1-12) */}
            <div className="lg:col-span-12 bg-[#4a2282] text-white rounded-3xl overflow-hidden border border-[#5f2a99] shadow-md flex flex-col lg:flex-row items-center group">
              <div className="p-8 sm:p-12 lg:w-3/5 space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#d9238c]/30 border border-[#d9238c]/40 text-[#f7eefa] text-xs font-medium">
                  <i className="fa-solid fa-book-open text-[#f7eefa]" aria-hidden="true"></i>
                  <span>Forlaget Golden Eagle</span>
                </div>
                <h3 className="font-serif text-3xl sm:text-4xl font-semibold text-[#faf5f9]">
                  Spirituelle bøger og kunst
                </h3>
                <p className="text-[#f7eefa] text-sm sm:text-base leading-relaxed max-w-xl">
                  Udforsk Lone Hvids to bøger <em>Præstindens Vej</em> og <em>Månetræer</em> samt håndlavet uld-, fjer- og silkekunst inspireret af naturen og myterne.
                </p>
                <div className="pt-2 flex flex-wrap items-center gap-4">
                  <Link
                    to="/spirituelle-boeger"
                    className="px-6 py-3 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white text-sm font-semibold shadow transition-all duration-200 inline-flex items-center gap-2"
                  >
                    <span>Se bøger og kunst</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                  </Link>
                  <Link
                    to="/foredrag"
                    className="px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold border border-white/20 transition-all duration-200"
                  >
                    Foredrag med Lone
                  </Link>
                </div>
              </div>
              <div className="lg:w-2/5 w-full h-64 lg:h-full min-h-[260px] relative overflow-hidden bg-[#381d6e] flex items-center justify-center p-6">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/mock-up-af-praestindens-vej-1024x944.png', { width: 500, quality: 85, fit: 'contain' })}
                  alt="Præstindens vej og Månetræer af Lone Hvid"
                  className="max-h-56 w-auto object-contain group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* 3. KORT PRÆSENTATION AF IBJERGET MED FORSIDE-HUS.WEBP */}
      <section className="py-20 sm:py-24 bg-[#faf5f9] border-y border-[#edd5e8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#f0d9eb] text-[#9b1b66] text-xs font-semibold uppercase tracking-wider">
                <i className="fa-solid fa-leaf" aria-hidden="true"></i>
                <span>Om behandlerfællesskabet</span>
              </div>
              
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1f142e] font-semibold tracking-tight leading-tight">
                Et fælles værdisæt om frihed og suverænitet
              </h2>

              <div className="prose prose-stone text-sm sm:text-base text-[#4e3568] leading-relaxed space-y-4">
                <p>
                  Vi har fundet sammen ud fra et fælles værdisæt om at skabe en verden, hvor den enkelte er fri og autentisk. Hvor det enkelte menneske er suverænt.
                </p>
                <p>
                  Suverænitet har mange betydninger. Det, vi lægger vægt på i denne sammenhæng, er evnen til at se sig selv, tage ansvar for sig selv, og leve et fuldt og glædesfuldt liv. Og når man tager ansvar for sig selv har man ofte brug for en eller flere, der selv går vejen, til støtte. Den støtte kan være ved at komme til fuldmåneaftener, foredrag eller individuelle behandlinger.
                </p>
                <p className="italic font-serif text-[#381d6e] text-lg">
                  "Gå lidt på opdagelse på hjemmesiden og mærk efter, om det er noget for dig."
                </p>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Link
                  to="/om-os"
                  className="px-6 py-3 rounded-full bg-[#381d6e] hover:bg-[#4a2282] text-white text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2"
                >
                  <span>Mød Lone og Niels</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>
                <Link
                  to="/kontakt"
                  className="text-sm font-semibold text-[#1f142e] hover:text-[#d9238c] transition-colors text-center sm:text-left py-1 sm:py-0"
                >
                  Kontakt os direkte →
                </Link>
              </div>
            </div>

            {/* HUSBILLEDET: forside-hus.webp */}
            <div className="lg:col-span-6 flex justify-center">
              <div className="relative max-w-md w-full">
                <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-white bg-white">
                  <img
                    src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/forside-hus.webp', { width: 900, quality: 90, fit: 'cover' })}
                    alt="Ibjerget – det lyse hus med mørkt tag, have og blå himmel"
                    className="w-full h-auto max-h-[580px] object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-4 right-4 bg-[#381d6e] text-[#f7eefa] px-4 py-2 rounded-2xl shadow-lg border border-[#5f2a99] text-xs">
                  <span className="font-serif font-bold text-white block">Ibjerget på Midtfyn</span>
                  <span>Ibjergvej 12, 5792 Årslev</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 4. KROPSTERAPI & MASSAGE DYBDESEKTION */}
      <section className="py-20 sm:py-24 bg-[#faf5f9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="rounded-3xl overflow-hidden shadow-lg border border-[#edd5e8]">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/behandlerrum.webp', { width: 900, quality: 85, fit: 'cover' })}
                  alt="Det lyse behandlerrum på Ibjerget"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-widest text-[#d9238c] block">
                Terapeut Niels Boye
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1f142e] font-semibold tracking-tight">
                Kropsterapi og kropsorienteret terapi
              </h2>
              <p className="text-base text-[#4e3568] leading-relaxed">
                Niels tager udgangspunkt i kroppen og arbejder intuitivt med en kombination af teknikker, muskelafspænding og energiarbejde for at genaktivere kroppens egen evne til at skabe balance.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <Link
                  to="/alt-ansigtsterapi"
                  className="p-4 rounded-2xl bg-white border border-[#edd5e8] hover:border-[#d9238c] transition-colors group"
                >
                  <h4 className="font-serif font-semibold text-[#1f142e] text-sm group-hover:text-[#d9238c]">
                    ALT Ansigtsterapi →
                  </h4>
                  <p className="text-xs text-[#9b1b66] mt-1">Forløsning gennem ansigtsmuskler</p>
                </Link>

                <Link
                  to="/kropsterapi"
                  className="p-4 rounded-2xl bg-white border border-[#edd5e8] hover:border-[#d9238c] transition-colors group"
                >
                  <h4 className="font-serif font-semibold text-[#1f142e] text-sm group-hover:text-[#d9238c]">
                    Kropsterapi →
                  </h4>
                  <p className="text-xs text-[#9b1b66] mt-1">Holdningskorrektion & energiarbejde</p>
                </Link>

                <Link
                  to="/healingmassage"
                  className="p-4 rounded-2xl bg-white border border-[#edd5e8] hover:border-[#d9238c] transition-colors group"
                >
                  <h4 className="font-serif font-semibold text-[#1f142e] text-sm group-hover:text-[#d9238c]">
                    Healingmassage →
                  </h4>
                  <p className="text-xs text-[#9b1b66] mt-1">En kærlighedserklæring til kroppen</p>
                </Link>

                <Link
                  to="/hotstone-massage"
                  className="p-4 rounded-2xl bg-white border border-[#edd5e8] hover:border-[#d9238c] transition-colors group"
                >
                  <h4 className="font-serif font-semibold text-[#1f142e] text-sm group-hover:text-[#d9238c]">
                    Hotstone-massage →
                  </h4>
                  <p className="text-xs text-[#9b1b66] mt-1">Varme lavasten & selvforkælelse</p>
                </Link>
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <a
                  href="tel:66157902"
                  className="px-7 py-3.5 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow transition-all duration-200 flex items-center gap-2"
                >
                  <span>Ring til Niels (66 15 79 02)</span>
                  <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 5. TAROTKORTTERAPI DYBDESEKTION */}
      <section className="py-20 sm:py-24 bg-[#faf5f9]/70 border-y border-[#edd5e8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-widest text-[#d9238c] block">
                Terapeut Lone Hvid
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1f142e] font-semibold tracking-tight">
                Tarotkortterapi som rum for refleksion og udvikling
              </h2>
              <p className="text-base text-[#4e3568] leading-relaxed">
                Lone anvender tarotkortenes rige symbolsprog som et spejl for det ubevidste og dit bevidste sind. En dyb, anerkendende proces uden spådom, med to fokuserede behandlingsformer:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="bg-white p-5 rounded-2xl border border-[#edd5e8]">
                  <i className="fa-solid fa-sparkles text-[#d9238c] text-lg mb-2" aria-hidden="true"></i>
                  <h4 className="font-serif font-semibold text-[#1f142e] text-base mb-1">Status på dine chakraer</h4>
                  <p className="text-xs text-[#4e3568] mb-3">Gennemgang af alle 9 chakraer for flow og skjulte indsigter.</p>
                  <Link to="/tarotkortterapi#chakraer" className="text-xs font-bold text-[#d9238c] hover:underline">
                    Læs mere & book (500 kr.) →
                  </Link>
                </div>
                <div className="bg-white p-5 rounded-2xl border border-[#edd5e8]">
                  <i className="fa-solid fa-bullseye text-[#d9238c] text-lg mb-2" aria-hidden="true"></i>
                  <h4 className="font-serif font-semibold text-[#1f142e] text-base mb-1">Fokus på problemløsning</h4>
                  <p className="text-xs text-[#4e3568] mb-3">Dyb proces omkring et konkret omdrejningspunkt eller en hindring.</p>
                  <Link to="/tarotkortterapi#problemloesning" className="text-xs font-bold text-[#d9238c] hover:underline">
                    Læs mere & book (700 kr.) →
                  </Link>
                </div>
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Link
                  to="/tarotkortterapi"
                  className="px-7 py-3.5 rounded-full bg-[#381d6e] hover:bg-[#4a2282] text-white font-semibold text-sm shadow transition-all duration-200 flex items-center gap-2"
                >
                  <span>Læs om Tarotkortterapi</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>
                <a
                  href="tel:30297902"
                  className="px-7 py-3.5 rounded-full bg-white hover:bg-[#faf5f9] text-[#1f142e] font-semibold text-sm border border-[#edd5e8] transition-all duration-200"
                >
                  Ring til Lone (30 29 79 02)
                </a>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-3xl overflow-hidden shadow-lg border border-[#edd5e8] bg-white">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/tarotkortterapi.webp', { width: 900, quality: 85, fit: 'cover' })}
                  alt="Tarotkort omkring chakramateriale på bordet"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 6. STEMNINGSGALLERI */}
      <GallerySection
        gallerySlug="ibjerget"
        title="Stemningsbilleder fra Ibjerget"
        subtitle="Rummene, naturen og fordybelsen på Midtfyn"
        limit={6}
      />

      {/* 7. MØD BEHANDLERNE: LONE & NIELS */}
      <section className="py-20 sm:py-24 bg-[#faf5f9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-14 space-y-2">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#d9238c] block">
              Mød behandlerne
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#1f142e]">
              To behandlere med hver deres ekspertise
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            
            {/* Lone Hvid Card */}
            <div className="bg-white rounded-3xl border border-[#edd5e8] p-8 shadow-sm flex flex-col justify-between space-y-6">
              <div className="flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
                <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-[#faf5f9] shadow shrink-0">
                  <img
                    src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/lone-hvid-profil-ny.webp', { width: 300, height: 300, quality: 85, fit: 'cover' })}
                    alt="Lone Hvid"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <h3 className="font-serif text-2xl font-semibold text-[#1f142e]">Lone Hvid</h3>
                  <p className="text-xs text-[#d9238c] font-semibold uppercase tracking-wider">Terapeut, kunstner og forfatter</p>
                  <p className="text-xs text-[#4e3568] mt-2">
                    NLP-terapeut, PD i psykologi, forfatter og præstinde. Tilbyder tarotkortterapi, foredrag samt spirituelle bøger og kunst.
                  </p>
                </div>
              </div>
              <div className="pt-4 border-t border-[#edd5e8] flex flex-wrap items-center justify-between gap-3 text-xs">
                <a href="tel:30297902" className="font-semibold text-[#1f142e] hover:text-[#d9238c] flex items-center gap-1.5">
                  <i className="fa-solid fa-phone text-[#d9238c]"></i>
                  <span>30 29 79 02</span>
                </a>
                <Link to="/om-os#lone" className="font-semibold text-[#d9238c] hover:underline">
                  Læs profil →
                </Link>
              </div>
            </div>

            {/* Niels Boye Card */}
            <div className="bg-white rounded-3xl border border-[#edd5e8] p-8 shadow-sm flex flex-col justify-between space-y-6">
              <div className="flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
                <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-[#faf5f9] shadow shrink-0">
                  <img
                    src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/niels-boye-profil.webp', { width: 300, height: 300, quality: 85, fit: 'cover' })}
                    alt="Niels Boye"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-serif text-2xl font-semibold text-[#1f142e]">Niels Boye</h3>
                  <p className="text-xs text-[#d9238c] font-semibold uppercase tracking-wider">Krops- og psykoterapeut</p>
                  <p className="text-xs text-[#4e3568] mt-2">
                    40 års erfaring med kropsorienteret terapi. Tilbyder ALT Ansigtsterapi, Kropsterapi, Healingmassage og Hotstonemassage.
                  </p>
                </div>
              </div>
              <div className="pt-4 border-t border-[#edd5e8] flex flex-wrap items-center justify-between gap-3 text-xs">
                <a href="tel:66157902" className="font-semibold text-[#1f142e] hover:text-[#d9238c] flex items-center gap-1.5">
                  <i className="fa-solid fa-phone text-[#d9238c]"></i>
                  <span>66 15 79 02</span>
                </a>
                <Link to="/om-os#niels" className="font-semibold text-[#d9238c] hover:underline">
                  Læs profil →
                </Link>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* 8. AFSLUTTENDE CTA: "Hvad har du brug for lige nu?" */}
      <section className="py-20 sm:py-24 bg-[#381d6e] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#e895c8] block">
            Tag det næste skridt
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-[#faf5f9] tracking-tight">
            Hvad har du brug for lige nu?
          </h2>
          <p className="text-base text-[#f7eefa] max-w-2xl mx-auto leading-relaxed">
            Uanset om du søger en kropsbehandling hos Niels, en dyb tarot-samtale med Lone, et foredrag eller Lones bøger og kunst, er du hjerteligt velkommen på Ibjerget.
          </p>

          <div className="pt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <Link
              to="/kontakt"
              className="p-5 rounded-2xl bg-[#4a2282] hover:bg-[#d9238c] text-white text-center transition-all duration-200 border border-[#5f2a99] flex flex-col justify-center items-center gap-2 group"
            >
              <i className="fa-solid fa-spa text-xl text-[#e895c8] group-hover:text-white transition-colors" aria-hidden="true"></i>
              <span className="font-semibold text-sm">Book behandling</span>
            </Link>

            <Link
              to="/tarotkortterapi"
              className="p-5 rounded-2xl bg-[#4a2282] hover:bg-[#d9238c] text-white text-center transition-all duration-200 border border-[#5f2a99] flex flex-col justify-center items-center gap-2 group"
            >
              <i className="fa-solid fa-cards-blank text-xl text-[#e895c8] group-hover:text-white transition-colors" aria-hidden="true"></i>
              <span className="font-semibold text-sm">Tarotkortterapi</span>
            </Link>

            <Link
              to="/spirituelle-boeger"
              className="p-5 rounded-2xl bg-[#4a2282] hover:bg-[#d9238c] text-white text-center transition-all duration-200 border border-[#5f2a99] flex flex-col justify-center items-center gap-2 group"
            >
              <i className="fa-solid fa-book-open text-xl text-[#e895c8] group-hover:text-white transition-colors" aria-hidden="true"></i>
              <span className="font-semibold text-sm">Spirituelle bøger og kunst</span>
            </Link>

            <Link
              to="/kontakt"
              className="p-5 rounded-2xl bg-[#4a2282] hover:bg-[#d9238c] text-white text-center transition-all duration-200 border border-[#5f2a99] flex flex-col justify-center items-center gap-2 group"
            >
              <i className="fa-solid fa-envelope text-xl text-[#e895c8] group-hover:text-white transition-colors" aria-hidden="true"></i>
              <span className="font-semibold text-sm">Kontakt os</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
